import { PlusIcon } from "@phosphor-icons/react/dist/csr/Plus";
import { TrashIcon } from "@phosphor-icons/react/dist/csr/Trash";
import { useRef } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { Input } from "@volca/ui/components/ui/input";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import type { TemplateSetting } from "./types";
import { type MapIssue, type MapRow, newRowId } from "./values";

export const MapEditor = ({
  setting,
  rows,
  disabled,
  invalid,
  describedBy,
  errorId,
  onChange,
}: {
  setting: TemplateSetting;
  rows: MapRow[];
  disabled?: boolean;
  invalid?: MapIssue;
  describedBy?: string;
  errorId?: string;
  onChange: (rows: MapRow[]) => void;
}) => {
  const addButton = useRef<HTMLButtonElement>(null);
  const add = () => {
    const id = newRowId();
    onChange([...rows, { id, name: "", value: "" }]);
    requestAnimationFrame(() => document.getElementById(`${id}-name`)?.focus());
  };
  const remove = (index: number) => {
    const next = rows.filter((_, position) => position !== index);
    const neighbor = next[Math.min(index, next.length - 1)];
    if (neighbor) document.getElementById(`${neighbor.id}-remove`)?.focus();
    else addButton.current?.focus();
    onChange(next);
  };
  const update = (id: string, change: Partial<MapRow>) =>
    onChange(rows.map((row) => (row.id === id ? { ...row, ...change } : row)));
  const unit =
    setting.value_type === "percent"
      ? "%"
      : setting.value_type === "money"
        ? "Amount"
        : "Value";
  const isInvalid = (row: MapRow, column: MapIssue["column"]) =>
    invalid?.rowId === row.id && invalid.column === column;
  const description = (row: MapRow, column: MapIssue["column"]) =>
    [describedBy, isInvalid(row, column) ? errorId : undefined]
      .filter(Boolean)
      .join(" ") || undefined;
  return (
    <Stack gap={3}>
      {rows.map((row, index) => (
        <Stack
          key={row.id}
          gap={2}
          className="rounded-lg border border-line p-3 sm:flex-row sm:items-end"
        >
          <Stack gap={1} className="min-w-0 flex-1">
            <Text
              variant="caption"
              render={<label htmlFor={`${row.id}-name`} />}
              tone={isInvalid(row, "name") ? "destructive" : undefined}
            >
              Name
            </Text>
            <Input
              id={`${row.id}-name`}
              aria-label={`Name: ${setting.label}, row ${index + 1}`}
              aria-invalid={isInvalid(row, "name") || undefined}
              aria-describedby={description(row, "name")}
              value={row.name}
              disabled={disabled}
              onChange={(event) => update(row.id, { name: event.target.value })}
            />
          </Stack>
          <Stack direction="row" gap={2} align="end" className="sm:w-36">
            <Stack gap={1} className="min-w-0 flex-1">
              <Text
                variant="caption"
                render={<label htmlFor={`${row.id}-value`} />}
                tone={isInvalid(row, "value") ? "destructive" : undefined}
              >
                {unit}
              </Text>
              <Input
                id={`${row.id}-value`}
                aria-label={`${unit}: ${setting.label}, ${row.name || `row ${index + 1}`}`}
                aria-invalid={isInvalid(row, "value") || undefined}
                aria-describedby={description(row, "value")}
                inputMode="decimal"
                value={row.value}
                disabled={disabled}
                onChange={(event) =>
                  update(row.id, { value: event.target.value })
                }
              />
            </Stack>
            <Button
              id={`${row.id}-remove`}
              type="button"
              size="icon"
              variant="ghost"
              aria-label={`Remove ${row.name || `row ${index + 1}`}`}
              disabled={disabled}
              onClick={() => remove(index)}
            >
              <TrashIcon />
            </Button>
          </Stack>
        </Stack>
      ))}
      <Stack direction="row">
        <Button
          ref={addButton}
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={add}
        >
          <PlusIcon />
          Add row
        </Button>
      </Stack>
    </Stack>
  );
};
